import { decimalToPercentage } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { formatCash } from '@/shared/utils/currency';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { extractAndFormatDate, extractAndFormatDollar, extractAndFormatNumber, extractAndFormatPercentage } from '@/shared/utils/fieldEditor';
import { AIFieldType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';

export const formatFieldValueForTemplate = (type: keyof typeof AIFieldType, value: string, commentary: string) => {
  switch (type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return value && commentary ? `${value} | ${commentary}` : (value && value) || (commentary && commentary);
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return value && commentary ? `${formatCash(+value)} | ${commentary}` : (value && formatCash(+value)) || (commentary && commentary);
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return value && commentary
        ? `${decimalToPercentage(value as string)} | ${commentary}`
        : (value && decimalToPercentage(value as string)) || (commentary && commentary);
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return value && commentary
        ? `${format(new Date(value), 'LLL d, yyyy')} | ${commentary}`
        : (value && format(new Date(value), 'LLL d, yyyy')) || (commentary && commentary);
    default:
      return value || '';
  }
};

export const formatAiFieldValueForTemplate = (type: keyof typeof AIFieldType, aiAnswer: string) => {
  switch (type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return extractAndFormatNumber(aiAnswer) ? `${extractAndFormatNumber(aiAnswer)} | ${aiAnswer}` : aiAnswer;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return extractAndFormatDollar(aiAnswer) ? `${formatCash(+extractAndFormatDollar(aiAnswer))} | ${aiAnswer}` : aiAnswer;
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return extractAndFormatPercentage(aiAnswer) ? `${decimalToPercentage(extractAndFormatPercentage(aiAnswer))} | ${aiAnswer}` : aiAnswer;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return extractAndFormatDate(aiAnswer) ? `${format(new Date(extractAndFormatDate(aiAnswer)), 'LLL d, yyyy')} | ${aiAnswer}` : aiAnswer;
    default:
      return aiAnswer || '';
  }
};

export const formatFirmAddress = (firmInfoData: FirmInfoType) => {
  const { address } = firmInfoData;
  if (!Object.values(address || {}).length) return '';

  const { streetAddress, city, state, zipCode } = address;

  return `${streetAddress} ${city ? ` ${city},` : ''} ${state} ${zipCode}`.trim();
};

export const formatFirmContactInfo = (firmInfoData: FirmInfoType) => {
  const { email, website, phone } = firmInfoData || {};
  const hasPhone = phone && Object.values(phone || {})?.length > 0;
  const phoneNumber = hasPhone ? formatUserPhoneNumber(phone.countryCode, phone.number) : '';

  const contactParts = [email, website, phoneNumber].filter(Boolean);

  return contactParts.join(' | ');
};

export const truncateString = (string: string | null, maxLength: number): string => {
  if (!string) return '';

  if (string.length <= maxLength) {
    return string;
  } else {
    return string.slice(0, maxLength) + '...';
  }
};

export const parseMarkdownHtmlImages = (html: string): { src: string; width?: number; height?: number }[] => {
  const imgTagRegex = /<img\s+[^>]*src="([^"]+)"[^>]*>/g;

  const matches: { src: string; width?: number; height?: number }[] = [];
  let match;

  while ((match = imgTagRegex.exec(html)) !== null) {
    const src = match[1];

    const widthMatch = match[0].match(/width=["']?(\d+)["']?/);
    const heightMatch = match[0].match(/height=["']?(\d+)["']?/);

    matches.push({
      src,
      width: widthMatch ? parseInt(widthMatch[1], 10) : undefined,
      height: heightMatch ? parseInt(heightMatch[1], 10) : undefined
    });
  }

  return matches;
};
